import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, Users, Activity as ActIcon, Clock, TrendingUp, MousePointerClick, Download } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ComposedChart, Bar, Legend } from "recharts";
import { PageLoading, PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

function Metric({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-slate-400">{label}</span><Icon className="h-4 w-4 text-cyan-dark" /></div>
      <div className="mt-2 font-heading text-3xl font-extrabold text-navy">{value}</div>
    </div>
  );
}

function TopList({ title, items }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <h3 className="mb-3 font-heading font-bold text-navy">{title}</h3>
      {(!items || items.length === 0) ? <p className="text-sm text-slate-400">—</p> : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-center justify-between gap-3 text-sm">
              <span className="truncate text-slate-600">{it.label}</span>
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-navy">{it.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminTraffic() {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [range, setRange] = useState("30");
  useEffect(() => { setD(null); api.get(`/admin/traffic?range=${range}`).then((r) => setD(r.data)); }, [range]);
  const [live, setLive] = useState(null);
  useEffect(() => {
    const tick = () => api.get("/admin/traffic/live").then((r) => setLive(r.data)).catch(() => {});
    tick(); const iv = setInterval(tick, 8000); return () => clearInterval(iv);
  }, []);
  const download = async (fmt) => {
    try {
      const r = await api.get(`/admin/traffic/report.${fmt}?range=${range}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `employguard-traffic.${fmt}`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  return (
    <div data-testid="admin-traffic">
      <PageHeader title={t("traffic.title")} subtitle={t("traffic.subtitle")} action={
        <div className="flex flex-wrap items-center gap-2">
          {live && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700" data-testid="traffic-live">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span>
              {live.active} {t("traffic.liveNow")}
            </span>
          )}
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => download("pdf")} data-testid="traffic-export-pdf"><Download className="mr-1 h-4 w-4" /> {t("traffic.exportPdf")}</Button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => download("csv")} data-testid="traffic-export-csv"><Download className="mr-1 h-4 w-4" /> {t("traffic.exportCsv")}</Button>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="h-9 w-36" data-testid="traffic-range"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">{t("traffic.r7")}</SelectItem>
              <SelectItem value="30">{t("traffic.r30")}</SelectItem>
              <SelectItem value="90">{t("traffic.r90")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      } />
      {!d ? <PageLoading /> : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Metric icon={Eye} label={t("traffic.pageviews")} value={d.pageviews} testid="tr-pageviews" />
            <Metric icon={Users} label={t("traffic.uniques")} value={d.uniques} testid="tr-uniques" />
            <Metric icon={ActIcon} label={t("traffic.activeNow")} value={d.active_now} testid="tr-active" />
            <Metric icon={Clock} label={t("traffic.avgDwell")} value={`${d.avg_dwell_seconds}s`} testid="tr-dwell" />
            <Metric icon={TrendingUp} label={t("traffic.registrations")} value={d.registrations} testid="tr-reg" />
            <Metric icon={TrendingUp} label={t("traffic.conversion")} value={`${d.conversion_rate}%`} testid="tr-conv" />
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <Metric icon={Eye} label={t("traffic.today")} value={d.windows.today} />
            <Metric icon={Eye} label={t("traffic.last7")} value={d.windows.d7} />
            <Metric icon={Eye} label={t("traffic.last30")} value={d.windows.d30} />
          </div>
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="mb-4 font-heading font-bold text-navy">{t("traffic.chart")}</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={d.by_day}><CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" /><XAxis dataKey="day" stroke="#94a3b8" fontSize={12} /><YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} /><Tooltip /><Line type="monotone" dataKey="count" stroke="#06B6D4" strokeWidth={2.5} dot={{ r: 2 }} /></LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="traffic-conversion-chart">
            <h3 className="mb-1 font-heading font-bold text-navy">{t("traffic.convChart")}</h3>
            <p className="mb-4 text-xs text-slate-400">{t("traffic.convChartSub")}</p>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={d.conversion_by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" stroke="#94a3b8" fontSize={12} />
                <YAxis yAxisId="left" stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" stroke="#06B6D4" fontSize={12} unit="%" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="uniques" name={t("traffic.uniques")} fill="#cbd5e1" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="left" dataKey="registrations" name={t("traffic.registrations")} fill="#0A192F" radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="rate" name={t("traffic.convRate")} stroke="#06B6D4" strokeWidth={2.5} dot={{ r: 2 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <TopList title={t("traffic.topPages")} items={d.top_pages} />
            <TopList title={t("traffic.topClicks")} items={d.top_clicks} />
          </div>
        </>
      )}
    </div>
  );
}
